import {
    createApi,
    fetchBaseQuery,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import qs from 'query-string';

import getEnvConfig from '../../../../../utils/config/env';
import {
    PromocodeFilters,
    PromocodeDTO,
    PromocodeRequest,
    PromocodeDeleteRequest,
    PromocodeResponse,
} from './promocodes.interface';
import { PaginatedResponse } from '../../../../communities/interfaces/PaginatedResponse';
import { API_PATH } from '../utils/options';

export const promocodeApi = createApi({
    reducerPath: 'promocode',
    baseQuery: fetchBaseQuery({
        baseUrl: getEnvConfig().env.API_URL,
        prepareHeaders: headers => {
            const token = localStorage.getItem('accessToken');
            headers.set('Authorization', `Bearer ${token}`);
            return headers;
        },
    }),

    tagTypes: ['Promocode'],
    endpoints: builder => ({
        createPromocode: builder.mutation<PromocodeDTO, PromocodeDTO>({
            query: body => ({
                url: `${API_PATH}`,
                method: 'POST',
                body: { ...body },
            }),
            invalidatesTags: ['Promocode'],
        }),

        readPromocodes: builder.query<
            PaginatedResponse<PromocodeDTO>,
            PromocodeFilters
        >({
            query: params => {
                const queryArgs = qs.stringify(params, {
                    arrayFormat: 'bracket',
                    skipNull: true,
                });
                return {
                    url: `${API_PATH}?${queryArgs}`,
                };
            },
            transformResponse: (
                res: { data: PromocodeDTO[] },
                meta: FetchBaseQueryMeta
            ) => {
                return {
                    items: res.data,
                    total:
                        Number(meta.response.headers.get('Items-Count')) || 0,
                };
            },
            providesTags: ['Promocode'],
        }),

        readPromocode: builder.query<PromocodeDTO, PromocodeRequest>({
            query: ({ id, shop_id }) => {
                return {
                    url: `${API_PATH}/${id}?shop_id=${shop_id}`,
                };
            },
            transformResponse: (res: { data: PromocodeDTO }) => {
                return res.data;
            },
            providesTags: ['Promocode'],
        }),

        updatePromocode: builder.mutation<PromocodeDTO, PromocodeDTO>({
            query: payload => ({
                url: `${API_PATH}/${payload.id}`,
                method: 'PUT',
                body: { ...payload },
            }),
            invalidatesTags: ['Promocode'],
        }),

        deletePromocode: builder.mutation<
            PromocodeResponse,
            PromocodeDeleteRequest
        >({
            query: ({ shop_id, ids }) => ({
                url: `${API_PATH}?shop_id=${shop_id}`,
                method: 'DELETE',
                body: { ids },
            }),
            invalidatesTags: ['Promocode'],
        }),
    }),
});

export const {
    useCreatePromocodeMutation,
    useLazyReadPromocodesQuery,
    useUpdatePromocodeMutation,
    useLazyReadPromocodeQuery,
    useDeletePromocodeMutation,
} = promocodeApi;
